import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

import baseUrl from "../../Environment";

export default function BlogList() {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Delete confirmation dialog state
  const [deleteTarget, setDeleteTarget] = useState(null); // blog object or null
  const [deleting, setDeleting] = useState(false);

  const loadBlogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${baseUrl}blog`);
      setBlogs(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load blogs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${baseUrl}blog/${deleteTarget._id}`);
      setBlogs((prev) => prev.filter((b) => b._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      setError("Failed to delete blog. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "—";

  return (
    <section className="bg-[#EEF4EA] min-h-screen py-16">
      <div className="sm:max-w-full md:max-w-[90%] xl:max-w-[75rem] w-full mx-auto">
        <div className="bg-white px-8 md:px-14 py-10 shadow-sm rounded">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-[#3A683F] font-lexend font-extralight text-[28px] md:text-[38px] uppercase">
              Blog Posts
            </h1>
            <Button
              component={Link}
              to="/blog/new"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#3A683F",
                color: "#fff",
                textTransform: "none",
                fontFamily: "Lexend, sans-serif",
                px: 3,
                py: 1,
                "&:hover": { backgroundColor: "#2f5533" },
              }}
            >
              New Blog
            </Button>
          </div>

          {error && (
            <div className="mb-6 px-4 py-3 rounded font-lexend text-[15px] bg-red-100 text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <CircularProgress sx={{ color: "#3A683F" }} />
            </div>
          ) : blogs.length === 0 ? (
            <p className="font-lexend text-gray-500 text-center py-16">
              No blog posts yet.
            </p>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F7FAF5" }}>
                    <TableCell sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 600, color: "#3A683F" }}>
                      Title
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 600, color: "#3A683F" }}>
                      Category
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 600, color: "#3A683F" }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 600, color: "#3A683F" }}>
                      Status
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 600, color: "#3A683F" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blogs.map((blog) => (
                    <TableRow key={blog._id} hover>
                      <TableCell sx={{ fontFamily: "Lexend, sans-serif" }}>{blog.title}</TableCell>
                      <TableCell sx={{ fontFamily: "Lexend, sans-serif" }}>
                        {blog.category || "—"}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Lexend, sans-serif" }}>
                        {formatDate(blog.date)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={blog.isPublished ? "Published" : "Draft"}
                          size="small"
                          sx={{
                            fontFamily: "Lexend, sans-serif",
                            backgroundColor: blog.isPublished ? "#E4F1E6" : "#F2F2F2",
                            color: blog.isPublished ? "#3A683F" : "#777",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => navigate(`/blog/edit/${blog._id}`)}
                          sx={{ color: "#3A683F" }}
                          aria-label="Edit blog"
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => setDeleteTarget(blog)}
                          sx={{ color: "#c0392b" }}
                          aria-label="Delete blog"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontFamily: "Lexend, sans-serif" }}>Delete blog post?</DialogTitle>
        <DialogContent sx={{ fontFamily: "Lexend, sans-serif" }}>
          This will permanently delete "{deleteTarget?.title}" and its images. This action
          cannot be undone.
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ textTransform: "none", fontFamily: "Lexend, sans-serif", color: "#555" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirmed}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              backgroundColor: "#c0392b",
              color: "#fff",
              "&:hover": { backgroundColor: "#a5311f" },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}